// @todo move to new file depending on Network
import type { AbiFunction, AbiParameter, AbiType } from "abitype";
import { type Abi, getAbiItem, parseAbi, toFunctionSelector } from "viem";

import {
  type AnyCell,
  Cell,
  type Cellified,
  type MapCell,
  type SheetProxy
} from "@okcontract/cells";
import {
  type ABI,
  type ABIMethod,
  ABIQuery,
  type ABIValue,
  type ABIValueType,
  type CacheQuery,
  ChainQuery,
  TokenCurrentChain
} from "@okcontract/coredata";
import {
  type LabelledTypeDefinition,
  type TypeDefinition,
  type TypeDefinitionFn,
  type TypeDefinitionNumber,
  mapTypeDefinitions
} from "@okcontract/fred";
import {
  type ASTNode,
  type Environment,
  type MonoType,
  Rational,
  newTypeObject,
  parseExpression,
  typeBoolean,
  typeList,
  typeNumber,
  typeString
} from "@okcontract/lambdascript";
import {
  type Address,
  type ChainAddress,
  type ChainType,
  ContractType,
  type LocalRPCSubscriber,
  type Network,
  getStringAddress,
  nativeBalance,
  storageAt
} from "@okcontract/multichain";
import type { LocalSubscriber } from "@scv/cache";
import { asyncReduce } from "@scv/utils";

import type { OKCore } from "./coreExecution";
import { approve_method, getBalance, getDecimals, getSymbol } from "./erc20";
import { fixedArrayRegexp, isArray, isFixedArray } from "./format";
import type { OKPage } from "./instance";
import { EnvKeyChain, EnvKeyPivot } from "./keys";
import { AddressExtension, typeAddress } from "./lambdascript";
import { resolveExpr } from "./resolve";
import { isRealWalletID } from "./wallet";

const fixedSizeArray = /\[(\d+)\]/;
const maxOf = {
  int8: 127,
  int16: 32767,
  int24: 256 ** 3 / 2 - 1,
  int32: 256 ** 4 / 2 - 1,
  int64: 256 ** 8 / 2 - 1,
  int128: 256 ** 16 / 2 - 1,
  int256: BigInt("2") ** BigInt("255") - BigInt("1"),
  uint8: 255,
  uint16: 65535,
  uint24: 256 ** 3 - 1,
  uint32: 256 ** 4 - 1,
  uint64: 256 ** 8 - 1,
  uint128: 256 ** 16 - 1,
  uint256: BigInt("2") ** BigInt("256") - BigInt("1")
};

export const ARG_PREFIX = "__arg";
const ARG_REGEX = /^__arg(\d+)$/;
export const anonymousArg = (i: number) => `${ARG_PREFIX}${i}`;
export const isAnonymousArg = (arg: string): boolean => ARG_REGEX.test(arg);
export const extractArgIndex = (arg: string): number | null => {
  const match = arg.match(ARG_REGEX);
  return match ? Number.parseInt(match[1], 10) : null;
};
export const getArgName = (pt: AbiParameter, i: number) =>
  pt?.name || anonymousArg(i);

/**
 * inputsPosition map inputs to values
 * @param fn
 * @param env
 * @returns
 */
export const inputsPosition = (fn: AbiFunction, env: Environment) =>
  fn?.inputs?.map((pt, i) => {
    const name = getArgName(pt, i);
    const value = env.value(name) as AnyCell<unknown>;
    return value || null;
  });

/**
 * isArrayDefined checks if an array contains no undefined values (recursively).
 * @param x
 * @returns
 */
const isArrayDefined = (x: unknown) =>
  !Array.isArray(x) ||
  x.reduce((acc, v) => acc && v !== undefined && isArrayDefined(v), true);

/**
 * isInputDefined checks if a given input is defined
 */
export const isInputDefined = (isOK: boolean, input: unknown): boolean =>
  isOK && input !== null && isArrayDefined(input);

/** make ABI creates a new ABI object */
export const makeABI = (
  chain: ChainType,
  addr: Address<Network>,
  abi: string[]
): ABI => {
  const stringified = JSON.stringify(abi);
  return {
    addr: { chain, addr, ty: "c" },
    abi: stringified,
    parsed: parseAbi(abi)
  };
};

export const mapHumanABI = (l: string[]) =>
  l
    .filter((v) => !v.startsWith("event "))
    .map((v: string) => (v.startsWith("function ") ? v : `function ${v}`));

// const logger = (name, v) => {
//   console.log(name, { v });
//   return v;
// };

/**
 * getABI returns an ABI
 * @param proxy
 * @param local
 * @param addr
 * @returns
 * @todo non-EVM
 */
export const getABI = (
  proxy: SheetProxy,
  local: LocalSubscriber<CacheQuery>,
  addr: AnyCell<ChainAddress>,
  parsedAbi?: Abi
): AnyCell<ABI> => {
  const query = addr.map(
    (_addr) =>
      _addr?.chain ? ABIQuery(_addr.chain, _addr?.px || _addr.addr) : null,
    "query"
  );
  // @todo check right proxy...
  // @todo useless if parsedAbi?
  const abi = local.unwrappedCell(query, "abi");
  const parsed = abi.map(
    (_abi) =>
      parsedAbi
        ? parsedAbi
        : _abi?.abi && _abi.abi !== "[]"
          ? (JSON.parse(_abi.abi) as Abi)
          : _abi?.human?.length
            ? parseAbi(mapHumanABI(_abi.human))
            : null,
    "parsed"
  );
  return proxy.map(
    [abi, parsed],
    (_abi, _parsed) => ({
      ..._abi,
      parsed: _parsed
    }),
    "getABI"
  );
};

/**
 * ABIMethodToASTMap takes an ABIMethod, discards its ABIMethodFields
 * and parses each ABIValue expression into its ASTNode.
 *
 * @param method the ABIMethod to parsed expressions.
 * @returns a map of ASTNode
 */
export const ABIMethodToASTMap = async (
  method: ABIMethod
): Promise<{ [name: string]: ASTNode }> => {
  if (!method) {
    return undefined;
  }
  const parsedPairs = await Promise.all(
    Object.entries(method).map(async ([name, value]) => {
      if (name.startsWith("@") || !value || value.v === undefined) {
        return null;
      }
      try {
        // @todo This hack reverts data (e.g. from cache) that has been
        // converted through `convertToAddressesAndRationals`.
        const str = value.v.toString();
        const parsed = await parseExpression(str, {
          ext: [AddressExtension]
        });
        return [name.toLowerCase(), parsed];
      } catch (error) {
        console.log("ABIMethodToASTMap failed:", { name, value, error });
        return null;
      }
    })
  );
  // Filter out any nulls from failed or skipped entries
  return Object.fromEntries(parsedPairs.filter(Boolean));
};

// {
//   return method
//     ? asyncReduce(
//         Object.entries(method),
//         async (acc, [name, value]) => {
//           try {
//             if (name.startsWith("@")) return acc;
//             if (value && value?.v !== undefined) {
//               return {
//                 ...acc,
//                 [name.toLowerCase()]: await parseExpression(value?.v, {
//                   ext: [AddressExtension]
//                 })
//               };
//             }
//             return acc;
//           } catch (error) {
//             console.log("ABIMethodToASTMap failed: ", { name, value, error });
//             return acc;
//           }
//         },
//         {}
//       )
//     : undefined;
// };

/**
 * abiArgDefinition return a definition for a given ABI argument
 * @param proxy
 * @param env
 * @param fn
 * @param a
 * @param pt
 * @returns
 */
export const abiArgDefinition = (
  instance: OKPage,
  env: AnyCell<Environment>,
  fn: AnyCell<AbiFunction>,
  a: AnyCell<ABIValue>,
  pt: AnyCell<AbiParameter>
): AnyCell<LabelledTypeDefinition> => {
  const proxy = instance.proxy;
  const abiValue = proxy.map(
    [a, pt],
    (_a, _pt) => ({
      ...(_a || {}),
      ty: _a?.ty || _pt?.type
    }),
    "abiArgDefinition.abiValue"
  ) as AnyCell<ABIValue>;
  return proxy.map(
    [pt, abiValue, env, fn],
    async (_pt, _a, _env, _fn) => {
      const _ty = _a?.ty;
      // console.log({ _ty, _pt, _a, _fn });
      if (_ty === undefined || typeof _ty !== "string") return null;
      if (_ty.endsWith("[]")) {
        const base = pt.map(
          (_pt) => ({ ..._pt, type: _pt.type.replace("[]", "") }),
          "base"
        );
        // @todo collect ?
        return {
          label: "",
          array: () => abiArgDefinition(instance, env, fn, a, base)
        };
      }
      const fixedArray = _ty.match(fixedSizeArray);
      if (fixedArray) {
        const base = pt.map(
          (_pt) => ({
            ..._pt,
            type: _pt.type.substring(0, _pt.type.indexOf("["))
          }),
          "base.fixed"
        );
        return {
          label: "",
          array: () => abiArgDefinition(instance, env, fn, a, base),
          max: Number(fixedArray[1])
        };
      }
      // @todo in this call abiValue is **not** Cellified
      return ABIValueToTypeDefinition(instance, abiValue, env, {
        pt,
        fn,
        label: fn?.name
      });
    },
    "abiArgDefinition"
  );
};

// @todo rewrite with cells, should return MapCell<LabelledTypeDefinition>
// https://github.com/okcontract/roadmap/issues/518
export const ABIValueToTypeDefinition = (
  instance: OKPage,
  // @todo should be only one type, probably cellified...
  value: AnyCell<ABIValue> | Cellified<ABIValue>,
  env: AnyCell<Environment>,
  opts?: {
    pt?: AnyCell<AbiParameter>;
    fn?: AnyCell<AbiFunction>;
    label?: string;
  }
): MapCell<LabelledTypeDefinition, false> => {
  const proxy = instance.proxy;

  const ty = value.map(
    (_value) => _value?.ty || null,
    "ABIValueToTypeDefinition.ty"
  ) as AnyCell<ABIValueType>;
  const pt = opts?.pt || (instance.null as AnyCell<AbiParameter>);

  const fn = opts?.fn || instance.null;
  // @todo type return a cell for the number cases
  return proxy.mapNoPrevious(
    [value, ty, pt, env, fn],
    // @ts-ignore can't unify AnyCell<LabelledTypeDefinition> and LabelledTypeDefinition
    async (_value, _ty, _pt, _env, _fn) => {
      if (!_ty) return null;
      switch (_ty) {
        case "bytes":
          return { label: _pt?.name || "", base: "string", isBinary: true };
        case "string":
          return {
            label: _pt?.name || "",
            base: "string",
            // FIXME: we should not rely on this hack, but on schema definitions
            // in ABIExtra or OKWidget instead
            isImg: _pt?.name?.startsWith("image") || undefined
          };
        case "bool":
          return { label: _pt?.name || "", base: "boolean" };
        case "number":
        case "int":
        case "int8":
        case "int16":
        case "int24":
        case "int32":
        case "int64":
        case "int128":
        case "int256":
        case "uint":
        case "uint8":
        case "uint16":
        case "uint24":
        case "uint32":
        case "uint64":
        case "uint128":
        case "uint256": {
          const address: AnyCell<Address<Network> | null> = _value?.a
            ? ((await _env?.evaluateString(
                _value.a instanceof Cell
                  ? await _value.a.consolidatedValue
                  : `$address(${_value.a})`
              )) as AnyCell<Address<Network>>)
            : _env.value(EnvKeyPivot)
              ? ((await _env?.evaluateString(
                  "$address($env.pivot)"
                )) as AnyCell<Address<Network>>)
              : instance.null;

          const ch = env.map((_env) => _env.value(EnvKeyChain) || null, "ch");
          const evm = proxy.map(
            [address, ch],
            (_addr, ch) =>
              ({
                addr: _addr,
                chain: ch,
                ty: ContractType
              }) as ChainAddress,
            "evm"
          );
          // @fixme below code is blocking notification of some cells once we drop drop the account
          // probably related to gc and cell destruction
          // const wallet = env.map((_env) => _env.values(EnvKeySelf))
          const balance = getBalance(
            instance.core,
            proxy,
            instance.rpc,
            evm,
            instance.core.WalletID
          );
          const unit = getSymbol(
            instance.local,
            instance.core,
            proxy,
            instance.rpc,
            evm,
            "ABIValueToTypeDefinition"
          );
          const decimals = getDecimals(
            instance.local,
            instance.core,
            proxy,
            instance.rpc,
            evm
          );
          const min: AnyCell<Rational> = proxy.map(
            [address],
            async (_addr) => {
              // console.log("ABIValueToTypeDefinition", { _addr, _value, _env });
              return _addr
                ? instance.zero
                : _value?.min
                  ? ((_env?.evaluateString(
                      // @todo remove this hack...
                      _value.min instanceof Cell
                        ? await _value.min.consolidatedValue
                        : _value.min
                    ) || instance.null) as unknown as AnyCell<Rational>)
                  : _ty.startsWith("u")
                    ? instance.zero
                    : instance.null;
            },
            "min"
          );
          // @todo we should have both max and rangeMax
          const max = proxy.map(
            [address, balance],
            async (address, balance) => {
              return address
                ? balance
                : _value?.max
                  ? _env?.evaluateString(
                      _value.max instanceof Cell
                        ? await _value.max.consolidatedValue
                        : _value.max
                    )
                  : maxOf?.[_ty] &&
                      typeof maxOf[_ty] === "number" &&
                      maxOf[_ty] < 256 ** 3 - 1
                    ? new Rational(maxOf[_ty])
                    : null;
            },
            "max"
          );
          const optional = address && _value?.opt === true;
          return proxy.map(
            [min, max, unit, decimals],
            (min, max, unit, decimals) => {
              const td = {
                label: _value?.l || _pt?.name || "",
                hidden: _value?.a !== undefined && address === undefined,
                base: "number",
                optional,
                min: min === null ? undefined : min,
                max: max === null ? undefined : max,
                // ...(min !== undefined && min !== "--" ? { min: min } : {}),
                // ...(max !== undefined && max !== "--" ? { max: max } : {}),
                ...(unit !== undefined ? { unit: unit } : {}),
                isBig: true,
                infinite: _fn?.name?.startsWith(approve_method) || false, // @todo
                decimals
              } as TypeDefinitionNumber;
              // console.log("ABIValueToTypeDefinition", { min, td, _value });
              return td;
            },
            "uint256"
          );
        }
        case "address":
          return {
            label: _pt?.name || "",
            base: "string",
            isAddress: true
          };
        case "token":
        case TokenCurrentChain:
        case "nft":
        case "contract":
        case "stake":
        case "swap":
          // FIXME: add address book type definition
          // case "abook": // address book // cspell:disable-line
          return {
            label: _pt?.name || "",
            base: "string",
            isAddress: _pt?.type === "address",
            search: _ty
          };
        case "image":
          return {
            label: _pt?.name || "",
            base: "string",
            isImg: true
          };
        case "tuple": {
          if (!("components" in _pt)) break;
          const object = {};
          for (let i = 0; i < _pt?.components?.length; i++) {
            const component = _pt.components?.[i];
            // @todo optional label
            const def = abiArgDefinition(
              instance,
              env,
              fn,
              instance.null, // @todo no ABI Value?
              proxy.new(component, "component")
            );
            object[component.name] = () => def;
          }
          return {
            label: "",
            object: mapTypeDefinitions(proxy, object, `tuple:${_pt.name}`),
            border: _pt.name
          };
        }
        case "enum": {
          const res = await resolveExpr(
            _env,
            _value?.em || "[]",
            proxy.new([])
          );
          return res.map(
            (en) =>
              ({
                label: _pt?.name || "",
                enum: en
              }) as LabelledTypeDefinition,
            "enum"
          );
        }
        default:
          return { label: _pt?.name || "", base: "string" };
      }
    },
    `ABIValueToTypeDefinition:${opts?.label}`
  );
};

/**
 * typeFromABIParam creates a λs type from an AbiParameter.
 * @param AbiParameter
 * @returns
 * @see The generated structure should match the schema definition that
 * will create inputs in `ABIValueToTypeDefinition`.
 */
export const typeFromABIParam = (
  param: AbiParameter,
  av: ABIValue
): MonoType | null => {
  const aux = (ty: AbiType, param: AbiParameter) => {
    if (isArray(ty))
      return typeList(aux(ty.substring(0, ty.length - 2) as AbiType, param));
    if (isFixedArray(ty))
      // @todo keep the count in later versions of Lambdascript
      return typeList(aux(ty.replace(fixedArrayRegexp, "") as AbiType, param));
    if (ty.startsWith("uint")) return typeNumber;
    if (ty.startsWith("int")) return typeNumber;
    if (ty === "bool") return typeBoolean;
    if (ty.startsWith("bytes")) return typeString;
    if (ty === "string") return typeString;
    if (ty === "address") return typeAddress;
    if (ty === "tuple") {
      if (!("components" in param)) throw "components not found";
      return newTypeObject(
        Object.fromEntries(
          param.components.map((ct) => [ct.name, aux(ct.type as AbiType, ct)])
        )
      );
    }
    // @todo mapping type
    throw new Error(`Unsupported AbiParameter type: ${ty}`);
  };
  if (param === null) {
    if (av?.ty) {
      if (
        av.ty === "number" ||
        av.ty.startsWith("int") ||
        av.ty.startsWith("uint")
      )
        return typeNumber;
      if (av.ty === "bool") return typeBoolean;
      if (av.ty === "string" || av.ty === "enum") return typeString;
      if (
        av.ty === "contract" ||
        av.ty === "nft" ||
        av.ty === "token" ||
        av.ty === TokenCurrentChain
      )
        return typeAddress;
    }
    throw new Error(`Unsupported AbiValue type: ${av?.ty}`);
  }
  return aux(param.type as AbiType, param);
};

export const findMethod = (m: ABIMethod, key: string): ABIValue | null => {
  const keyLow = key.toLowerCase();
  const id = Object.keys(m).find((k) => k.toLowerCase() === keyLow);
  // @todo exclude other keys?
  return (m?.[id] as unknown as ABIValue) || null;
};

/**
 * $valueDefinition returns a TypeDefinitionFn for the special $value.
 * @returns
 */
export const $valueDefinition = (
  instance: OKPage,
  abi: AnyCell<ABI>
): TypeDefinitionFn => {
  const proxy = instance.proxy;
  const core = instance.core;
  const chainID = abi.map((_abi) => _abi.addr.chain, "$vD.chainID");
  const owner = core.WalletID.map(
    (_walletID) => (isRealWalletID(_walletID) && _walletID) || null,
    "$vD.owner"
  );
  // @todo balance could be part of Core?
  const balance = nativeBalance(proxy, instance.rpc, chainID, owner);
  const chain = proxy.map(
    [chainID, core.Chains],
    (id, chains) => chains[ChainQuery(id)] || null,
    "$vD.chain"
  );
  const tokenQuery = chain.map((_chain) => _chain.currency, "$vD.tQ");
  const token = instance.local.unwrappedCell(tokenQuery, "$vD.token");
  const def = proxy.map(
    [token, balance],
    (_token, _balance) => {
      return {
        label: "Amount",
        base: "number",
        min: new Rational(0),
        max: new Rational(_balance?.toString() || 0),
        isBig: true,
        decimals: _token?.decimals || new Rational(18),
        unit: _token?.symbol
      } as LabelledTypeDefinition;
    },
    "$vD.def"
  );
  // console.log({ def });
  return () => def;
};

export const mergeExtraABIValue = (
  proxy: SheetProxy,
  from: AnyCell<TypeDefinition>,
  m: AnyCell<ABIMethod>, // @todo Cellified<ABIMethod>
  key: string
): MapCell<LabelledTypeDefinition, boolean> =>
  proxy.map(
    [from, m],
    (_from, _m) => {
      const av = findMethod(_m, key);
      return {
        ..._from,
        ...(av
          ? {
              label: av?.l || key,
              desc: av?.desc || key,
              pl: av?.pl || key
            }
          : undefined)
      };
    },
    `mergeExtraABIValue:${key}`
  );

/**
 * getAbiFunction searches in an ABI a given method or return the first
 * with at least one input
 * @param abi
 * @param name optional method name
 * @returns AbiFunction
 */
export const getAbiFunction = (abi: Abi, name?: string) =>
  (abi
    ? name
      ? getAbiItem({
          abi,
          name: name.includes("(") ? toFunctionSelector(name) || null : name
        }) || null
      : abi
        ? abi.find(
            (_meth) =>
              _meth.type === "function" &&
              (_meth?.inputs?.length > 0 || _meth.stateMutability === "payable")
          ) || null
        : null
    : null) as AbiFunction;

const EIP1967_IMPLEMENTATION_SLOT =
  "0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc" as const;
const EIP1967_BEACON_SLOT =
  "0xa3f0ed1dcd44880f7d35470dcecdb40c63a77ab1b2b5b648b5a0aa798f7f6ee1" as const;

export const proxyImplementation = (
  proxy: SheetProxy,
  rpc: LocalRPCSubscriber,
  chain: AnyCell<ChainType>,
  addr: AnyCell<Address<Network>>
) =>
  storageAt(
    proxy,
    rpc,
    chain,
    addr,
    proxy.new(EIP1967_IMPLEMENTATION_SLOT, "slot") // @todo move to defaults
  ).map((v) => (v ? getStringAddress(`0x${v.slice(-40)}`) : null));
